'use client'

import { forwardRef, useState, useCallback, useMemo } from 'react'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, MoreHorizontal } from 'lucide-react'

/**
 * BIRHAUS Pagination Component
 * 
 * Enhanced pagination implementing all 10 BIRHAUS principles:
 * - Spanish-first labels and navigation text
 * - Progressive disclosure of page numbers (max 7 visible)
 * - Cognitive load reduction with clear navigation
 * - Accessibility compliance (WCAG AA+)
 * - Performance optimized rendering
 * - Mobile-friendly responsive design
 * - IPU Admin specific page size options
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface PaginationInfo {
  currentPage: number
  totalPages: number
  totalItems: number
  itemsPerPage: number
  startItem: number
  endItem: number
}

export interface BirhausPaginationProps {
  // BIRHAUS Principle #7: Bilingual by design
  label?: string
  labelEs?: string
  labelEn?: string
  
  // Pagination data
  currentPage: number
  totalItems: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  
  // Page size controls
  pageSizeOptions?: number[]
  onPageSizeChange?: (pageSize: number) => void
  showPageSizeSelector?: boolean
  
  // BIRHAUS Principle #4: Progressive disclosure
  maxVisiblePages?: number // Default 7 (Miller's Law)
  showQuickJump?: boolean
  showFirstLast?: boolean
  
  // BIRHAUS Principle #6: Accessibility = dignity
  disabled?: boolean
  
  // Appearance
  variant?: 'default' | 'minimal' | 'compact'
  size?: 'sm' | 'md' | 'lg'
  
  // Labels customization
  previousLabel?: string
  nextLabel?: string
  firstLabel?: string
  lastLabel?: string
  ofLabel?: string // "of" in "Page 1 of 10"
  itemsLabel?: string // "items" in "1-10 of 100 items"
  
  // Locale
  locale?: 'es' | 'en'
  
  // Mobile optimization
  mobileVariant?: 'compact' | 'minimal' | 'hidden'
  
  // IPU Admin specific
  showItemsInfo?: boolean
  showQuickActions?: boolean
}

export const BirhausPagination = forwardRef<HTMLDivElement, BirhausPaginationProps>(({
  // Labels (Spanish-first)
  label,
  labelEs,
  labelEn,
  
  // Core pagination
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  
  // Page size
  pageSizeOptions = [10, 25, 50, 100],
  onPageSizeChange,
  showPageSizeSelector = true,
  
  // Progressive disclosure
  maxVisiblePages = 7,
  showQuickJump = false,
  showFirstLast = true,
  
  // Basic props
  disabled = false,
  
  // Appearance
  variant = 'default',
  size = 'md',
  
  // Custom labels
  previousLabel,
  nextLabel,
  firstLabel,
  lastLabel,
  ofLabel,
  itemsLabel,
  
  // Locale
  locale = 'es',
  
  // Mobile
  mobileVariant = 'compact',
  
  // IPU Admin
  showItemsInfo = true,
  showQuickActions = false
}, ref) => {
  // Local state for quick jump
  const [jumpToPage, setJumpToPage] = useState('')
  const [showJumpInput, setShowJumpInput] = useState(false)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const getDisplayText = useCallback((
    primary: string | undefined,
    spanish: string,
    english: string
  ): string => {
    if (primary) return primary
    return locale === 'es' ? spanish : english
  }, [locale])
  
  // Localized labels
  const labels = useMemo(() => ({
    previous: getDisplayText(previousLabel, 'Anterior', 'Previous'),
    next: getDisplayText(nextLabel, 'Siguiente', 'Next'),
    first: getDisplayText(firstLabel, 'Primera', 'First'),
    last: getDisplayText(lastLabel, 'Última', 'Last'),
    of: getDisplayText(ofLabel, 'de', 'of'),
    items: getDisplayText(itemsLabel, 'elementos', 'items'),
    page: locale === 'es' ? 'Página' : 'Page',
    goTo: locale === 'es' ? 'Ir a página' : 'Go to page',
    showing: locale === 'es' ? 'Mostrando' : 'Showing',
    to: locale === 'es' ? 'a' : 'to',
    itemsPerPage: locale === 'es' ? 'elementos por página' : 'items per page',
    jump: locale === 'es' ? 'Saltar' : 'Jump'
  }), [getDisplayText, previousLabel, nextLabel, firstLabel, lastLabel, ofLabel, itemsLabel, locale])
  
  // Calculate pagination info
  const paginationInfo = useMemo((): PaginationInfo => {
    const totalPages = Math.ceil(totalItems / itemsPerPage)
    const startItem = (currentPage - 1) * itemsPerPage + 1
    const endItem = Math.min(currentPage * itemsPerPage, totalItems)
    
    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
      startItem,
      endItem
    }
  }, [currentPage, totalItems, itemsPerPage])
  
  // Calculate visible page numbers (progressive disclosure)
  const visiblePages = useMemo(() => {
    const { totalPages } = paginationInfo
    const maxPages = Math.min(maxVisiblePages, totalPages)
    
    if (totalPages <= maxPages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    
    const halfMax = Math.floor(maxPages / 2)
    let start = Math.max(1, currentPage - halfMax)
    const end = Math.min(totalPages, start + maxPages - 1)
    
    // Adjust if we're near the end
    if (end - start + 1 < maxPages) {
      start = Math.max(1, end - maxPages + 1)
    }
    
    const pages: (number | 'ellipsis')[] = []
    
    // Add first page and ellipsis if needed
    if (start > 1) {
      pages.push(1)
      if (start > 2) {
        pages.push('ellipsis')
      }
    }
    
    // Add visible pages
    for (let i = start; i <= end; i++) {
      pages.push(i)
    }
    
    // Add ellipsis and last page if needed
    if (end < totalPages) {
      if (end < totalPages - 1) {
        pages.push('ellipsis')
      }
      pages.push(totalPages)
    }
    
    return pages
  }, [paginationInfo, maxVisiblePages, currentPage])
  
  // Navigation handlers
  const handlePageChange = useCallback((page: number) => {
    if (disabled) return
    
    const { totalPages } = paginationInfo
    const newPage = Math.max(1, Math.min(page, totalPages))
    
    if (newPage !== currentPage) {
      onPageChange(newPage)
    }
  }, [disabled, paginationInfo, currentPage, onPageChange])
  
  const handlePrevious = useCallback(() => {
    handlePageChange(currentPage - 1)
  }, [handlePageChange, currentPage])
  
  const handleNext = useCallback(() => {
    handlePageChange(currentPage + 1)
  }, [handlePageChange, currentPage])
  
  const handleFirst = useCallback(() => {
    handlePageChange(1)
  }, [handlePageChange])
  
  const handleLast = useCallback(() => {
    handlePageChange(paginationInfo.totalPages)
  }, [handlePageChange, paginationInfo.totalPages])
  
  // Quick jump handler
  const handleQuickJump = useCallback(() => {
    const page = parseInt(jumpToPage, 10)
    if (!isNaN(page)) {
      handlePageChange(page)
      setJumpToPage('')
      setShowJumpInput(false)
    }
  }, [jumpToPage, handlePageChange])
  
  // Page size change handler
  const handlePageSizeChange = useCallback((newPageSize: number) => {
    if (onPageSizeChange) {
      onPageSizeChange(newPageSize)
      // Adjust current page if needed
      const newTotalPages = Math.ceil(totalItems / newPageSize)
      if (currentPage > newTotalPages) {
        onPageChange(newTotalPages)
      }
    }
  }, [onPageSizeChange, totalItems, currentPage, onPageChange])
  
  // Style classes
  const sizeClasses = {
    sm: {
      button: 'px-2 py-1 text-sm',
      text: 'text-sm',
      select: 'text-sm px-2 py-1'
    },
    md: {
      button: 'px-3 py-2 text-sm',
      text: 'text-sm',
      select: 'text-sm px-3 py-2'
    },
    lg: {
      button: 'px-4 py-2 text-base',
      text: 'text-base',
      select: 'text-base px-3 py-2'
    }
  }
  
  const variantClasses = {
    default: {
      container: 'bg-white border border-gray-200 rounded-lg',
      button: 'border border-gray-300 hover:bg-gray-50',
      activePage: 'bg-primary-600 text-white border-primary-600',
      inactivePage: 'text-gray-700 hover:bg-gray-50'
    },
    minimal: {
      container: '',
      button: 'hover:bg-gray-100',
      activePage: 'bg-primary-100 text-primary-700',
      inactivePage: 'text-gray-600 hover:bg-gray-100'
    },
    compact: {
      container: 'bg-gray-50 rounded-md',
      button: 'hover:bg-white hover:shadow-sm',
      activePage: 'bg-primary-600 text-white',
      inactivePage: 'text-gray-700 hover:bg-white'
    }
  }
  
  const currentSizeClasses = sizeClasses[size]
  const currentVariantClasses = variantClasses[variant]
  
  // If no items, don't render
  if (totalItems === 0) {
    return null
  }
  
  // Mobile view
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
  
  if (isMobile && mobileVariant === 'hidden') {
    return null
  }
  
  if (isMobile && mobileVariant === 'minimal') {
    return (
      <div ref={ref} className="birhaus-pagination">
        <div className="flex items-center justify-between">
          <button
            onClick={handlePrevious}
            disabled={disabled || currentPage === 1}
            className={cn(
              'flex items-center space-x-1 rounded-md transition-colors',
              currentSizeClasses.button,
              currentVariantClasses.button,
              (disabled || currentPage === 1) && 'opacity-50 cursor-not-allowed'
            )}
          >
            <ChevronLeft className="h-4 w-4" />
            <span>{labels.previous}</span>
          </button>
          
          <span className={cn('font-medium', currentSizeClasses.text)}>
            {currentPage} {labels.of} {paginationInfo.totalPages}
          </span>
          
          <button
            onClick={handleNext}
            disabled={disabled || currentPage === paginationInfo.totalPages}
            className={cn(
              'flex items-center space-x-1 rounded-md transition-colors',
              currentSizeClasses.button,
              currentVariantClasses.button,
              (disabled || currentPage === paginationInfo.totalPages) && 'opacity-50 cursor-not-allowed'
            )}
          >
            <span>{labels.next}</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    )
  }
  
  if (isMobile && mobileVariant === 'compact') {
    return (
      <div ref={ref} className="birhaus-pagination">
        <div className={cn('p-3 space-y-3', currentVariantClasses.container)}>
          {/* Items info */}
          {showItemsInfo && (
            <div className={cn('text-center', currentSizeClasses.text, 'text-gray-600')}>
              {labels.showing} {paginationInfo.startItem}-{paginationInfo.endItem} {labels.of} {paginationInfo.totalItems} {labels.items}
            </div>
          )}
          
          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrevious}
              disabled={disabled || currentPage === 1}
              className={cn(
                'flex items-center space-x-1 rounded-md transition-colors',
                currentSizeClasses.button,
                currentVariantClasses.button,
                (disabled || currentPage === 1) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <ChevronLeft className="h-4 w-4" />
              <span>{labels.previous}</span>
            </button>
            
            <span className={cn('font-medium', currentSizeClasses.text)}>
              {labels.page} {currentPage} {labels.of} {paginationInfo.totalPages}
            </span>
            
            <button
              onClick={handleNext}
              disabled={disabled || currentPage === paginationInfo.totalPages}
              className={cn(
                'flex items-center space-x-1 rounded-md transition-colors',
                currentSizeClasses.button,
                currentVariantClasses.button,
                (disabled || currentPage === paginationInfo.totalPages) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <span>{labels.next}</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          
          {/* Page size selector */}
          {showPageSizeSelector && onPageSizeChange && pageSizeOptions.length > 1 && (
            <div className="flex items-center justify-center space-x-2">
              <span className={cn(currentSizeClasses.text, 'text-gray-600')}>
                {labels.itemsPerPage}:
              </span>
              <select
                value={itemsPerPage}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
                disabled={disabled}
                className={cn(
                  'border border-gray-300 rounded-md bg-white',
                  currentSizeClasses.select,
                  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50',
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>
    )
  }
  
  // Desktop view
  return (
    <div ref={ref} className="birhaus-pagination">
      <div className={cn('p-4 space-y-4', currentVariantClasses.container)}>
        {/* Items info and page size selector */}
        <div className="flex items-center justify-between">
          {/* Items info */}
          {showItemsInfo && (
            <div className={cn(currentSizeClasses.text, 'text-gray-600')}>
              {labels.showing} <span className="font-medium">{paginationInfo.startItem}</span> {labels.to} <span className="font-medium">{paginationInfo.endItem}</span> {labels.of} <span className="font-medium">{paginationInfo.totalItems}</span> {labels.items}
            </div>
          )}
          
          {/* Page size selector */}
          {showPageSizeSelector && onPageSizeChange && pageSizeOptions.length > 1 && (
            <div className="flex items-center space-x-2">
              <span className={cn(currentSizeClasses.text, 'text-gray-600')}>
                {labels.itemsPerPage}:
              </span>
              <select
                value={itemsPerPage}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
                disabled={disabled}
                className={cn(
                  'border border-gray-300 rounded-md bg-white',
                  currentSizeClasses.select,
                  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50',
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        
        {/* Navigation */}
        <div className="flex items-center justify-between">
          {/* Previous navigation */}
          <div className="flex items-center space-x-1">
            {showFirstLast && (
              <button
                onClick={handleFirst}
                disabled={disabled || currentPage === 1}
                className={cn(
                  'flex items-center space-x-1 rounded-md transition-colors',
                  currentSizeClasses.button,
                  currentVariantClasses.button,
                  (disabled || currentPage === 1) && 'opacity-50 cursor-not-allowed'
                )}
                title={labels.first}
              >
                <ChevronsLeft className="h-4 w-4" />
                <span className="hidden sm:inline">{labels.first}</span>
              </button>
            )}
            
            <button
              onClick={handlePrevious}
              disabled={disabled || currentPage === 1}
              className={cn(
                'flex items-center space-x-1 rounded-md transition-colors',
                currentSizeClasses.button,
                currentVariantClasses.button,
                (disabled || currentPage === 1) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <ChevronLeft className="h-4 w-4" />
              <span>{labels.previous}</span>
            </button>
          </div>
          
          {/* Page numbers */}
          <div className="flex items-center space-x-1">
            {visiblePages.map((page, index) => (
              page === 'ellipsis' ? (
                <div key={`ellipsis-${index}`} className="px-2">
                  <MoreHorizontal className="h-4 w-4 text-gray-400" />
                </div>
              ) : (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  disabled={disabled}
                  className={cn(
                    'rounded-md transition-colors font-medium',
                    currentSizeClasses.button,
                    page === currentPage
                      ? currentVariantClasses.activePage
                      : cn(currentVariantClasses.inactivePage, currentVariantClasses.button),
                    disabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {page}
                </button>
              )
            ))}
          </div>
          
          {/* Next navigation */}
          <div className="flex items-center space-x-1">
            <button
              onClick={handleNext}
              disabled={disabled || currentPage === paginationInfo.totalPages}
              className={cn(
                'flex items-center space-x-1 rounded-md transition-colors',
                currentSizeClasses.button,
                currentVariantClasses.button,
                (disabled || currentPage === paginationInfo.totalPages) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <span>{labels.next}</span>
              <ChevronRight className="h-4 w-4" />
            </button>
            
            {showFirstLast && (
              <button
                onClick={handleLast}
                disabled={disabled || currentPage === paginationInfo.totalPages}
                className={cn(
                  'flex items-center space-x-1 rounded-md transition-colors',
                  currentSizeClasses.button,
                  currentVariantClasses.button,
                  (disabled || currentPage === paginationInfo.totalPages) && 'opacity-50 cursor-not-allowed'
                )}
                title={labels.last}
              >
                <span className="hidden sm:inline">{labels.last}</span>
                <ChevronsRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        
        {/* Quick jump */}
        {showQuickJump && paginationInfo.totalPages > maxVisiblePages && (
          <div className="flex items-center justify-center space-x-2">
            {!showJumpInput ? (
              <button
                onClick={() => setShowJumpInput(true)}
                disabled={disabled}
                className={cn(
                  'text-primary-600 hover:text-primary-700 underline',
                  currentSizeClasses.text,
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                {labels.goTo}...
              </button>
            ) : (
              <>
                <span className={cn(currentSizeClasses.text, 'text-gray-600')}>
                  {labels.goTo}:
                </span>
                <input
                  type="number"
                  min="1"
                  max={paginationInfo.totalPages}
                  value={jumpToPage}
                  onChange={(e) => setJumpToPage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleQuickJump()}
                  disabled={disabled}
                  className={cn(
                    'w-16 border border-gray-300 rounded-md text-center',
                    currentSizeClasses.select,
                    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50',
                    disabled && 'opacity-50 cursor-not-allowed'
                  )}
                  placeholder="1"
                />
                <button
                  onClick={handleQuickJump}
                  disabled={disabled || !jumpToPage}
                  className={cn(
                    'rounded-md transition-colors',
                    currentSizeClasses.button,
                    'bg-primary-600 text-white hover:bg-primary-700',
                    (disabled || !jumpToPage) && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {labels.jump}
                </button>
                <button
                  onClick={() => {
                    setShowJumpInput(false)
                    setJumpToPage('')
                  }}
                  disabled={disabled}
                  className={cn(
                    'text-gray-400 hover:text-gray-600',
                    currentSizeClasses.text,
                    disabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  ×
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
})

BirhausPagination.displayName = 'BirhausPagination'

// Hook for enhanced pagination functionality
export const useBirhausPagination = (
  totalItems: number,
  initialPageSize: number = 25
) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  
  const totalPages = Math.ceil(totalItems / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = Math.min(startIndex + pageSize, totalItems)
  
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)))
  }, [totalPages])
  
  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize)
    // Adjust current page if needed
    const newTotalPages = Math.ceil(totalItems / newPageSize)
    if (currentPage > newTotalPages) {
      setCurrentPage(newTotalPages)
    }
  }, [totalItems, currentPage])
  
  const reset = useCallback(() => {
    setCurrentPage(1)
  }, [])
  
  return {
    currentPage,
    pageSize,
    totalPages,
    startIndex,
    endIndex,
    handlePageChange,
    handlePageSizeChange,
    reset,
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1
  }
}

// IPU Admin specific pagination configurations
export const ipuAdminPaginationConfig = {
  members: {
    defaultPageSize: 25,
    pageSizeOptions: [10, 25, 50, 100],
    showQuickJump: true
  },
  donations: {
    defaultPageSize: 50,
    pageSizeOptions: [25, 50, 100, 200],
    showQuickJump: true
  },
  events: {
    defaultPageSize: 10,
    pageSizeOptions: [5, 10, 20, 50],
    showQuickJump: false
  },
  reports: {
    defaultPageSize: 20,
    pageSizeOptions: [10, 20, 50],
    showQuickJump: false
  }
}